import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, createSelector } from "@ngrx/store";
import { from, of } from "rxjs";
import { catchError, map, switchMap, tap, filter, exhaustMap, mergeMap } from "rxjs/operators";

import { isAegisArtifact } from "src/app/artifacts/artifact.util";
import { DocumentHelperService } from "src/app/services/document-helper.service";
import { FreyaHelperService } from "src/app/services/freya-helper.service";

import { DeleteInvoicesGQL, DeleteInvoicesMutationVariables, ExportInvoiceToQuickbooksGQL, ExportInvoiceToQuickbooksMutationVariables, FinalizeInvoicesGQL, GetInvoiceLinksGQL, SendInvoicesGQL, UpdateInvoicesGQL, UpdateInvoicesMutationVariables } from "../../../generated/graphql.generated";
import { isDraftInvoice, isFinalizedInvoice, outdatedInvoiceReason, shouldRefreshInvoiceDocument } from "../../invoices/invoices.utils";
import { DetailsHelperService } from "../../services/details-helper.service";


import { FreyaNotificationsService } from "../../services/freya-notifications.service";
import { isDocumentRefreshRefusedError } from "../../utilities/errors.util";
import { jobToolFeature, selectActionsMenuInvoice, selectInvoices, selectJobId, selectInvoicesDictionary } from "../job-tool.reducer";

import { InvoiceRefreshIntent, JobInvoiceActions } from "./job-invoice.actions";

export const openApplyTransactionToInvoiceMenu = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.payInvoiceMenuItemClicked),
      concatLatestFrom(() => store.select(selectActionsMenuInvoice)),
      tap(([ _action, selectedInvoice ]) => documentHelper.payInvoice(selectedInvoice)),
    );
}, { functional: true, dispatch: false });

// Requirement: a document is never served stale while its engine can still
// rebuild it. An outdated invoice refreshes on its way to whatever the user
// asked for, rather than asking them to press an update button first —
// including a signed Aegis invoice, where the engine decides whether the drift
// is refreshable (aegis.contextDelta). When it is not, staff still get the
// stored document, with a warning not to send it to the customer.
const refreshBeforeOpening = (intent: 'preview' | 'download') => (
  actions$: Actions,
  store: Store,
  trigger: any,
  proceed: () => Action,
) => actions$.pipe(
  ofType(trigger),
  concatLatestFrom(() => [
    store.select(selectActionsMenuInvoice),
    store.select(jobToolFeature.selectActionsMenuInvoiceId),
  ]),
  map(([ _, selectedInvoice, invoiceId ]) => {
    return shouldRefreshInvoiceDocument(selectedInvoice)
      ? JobInvoiceActions.updateInvoiceRequested({ invoiceId, then: intent })
      : proceed();
  }),
);

export const initiateInvoiceUpdateOrOpeningPreviewDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return refreshBeforeOpening('preview')(
    actions$,
    store,
    JobInvoiceActions.previewInvoiceMenuItemClicked,
    () => JobInvoiceActions.previewInvoiceRequested(),
  );
}, { functional: true, dispatch: true });

export const initiateInvoiceUpdateOrDownload = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return refreshBeforeOpening('download')(
    actions$,
    store,
    JobInvoiceActions.downloadInvoiceMenuItemClicked,
    () => JobInvoiceActions.downloadInvoiceRequested(),
  );
}, { functional: true, dispatch: true });

export const openInvoiceInPreviewDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
  return actions$.pipe(
    ofType(
      JobInvoiceActions.previewInvoiceRequested,
      JobInvoiceActions.updateInvoiceSuccess,
      JobInvoiceActions.updateInvoiceRefused,
    ),
    filter((action: any) => {
      if (action.type === JobInvoiceActions.previewInvoiceRequested.type) {
        return true;
      }
      return action.then === 'preview';
    }),
    concatLatestFrom((action) => store.select(createSelector(
      selectInvoicesDictionary,
      selectActionsMenuInvoice,
      (invoices, selectedInvoice) => {
        if (action.type !== JobInvoiceActions.previewInvoiceRequested.type) {
          return invoices[action.invoiceId];
        }
        return selectedInvoice;
      },
    ))),
    filter(([ _action, selectedInvoice ]) => Boolean(selectedInvoice?.artifacts?.[0])),
    tap(([ action, selectedInvoice ]) => freyaHelper.openInDialog(selectedInvoice.artifacts[0] as any, undefined, {
      outdatedReason: action.type === JobInvoiceActions.updateInvoiceRefused.type ? action.reason : undefined,
    })),
  );
}, { functional: true, dispatch: false });

export const updateInvoice = createEffect((
  actions$ = inject(Actions),
  updateInvoicesGQL = inject(UpdateInvoicesGQL),
  store = inject(Store),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.updateInvoiceRequested),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectActionsMenuInvoiceId),
        store.select(selectJobId),
      ]),
      map(([ action, invoiceId, jobId ]): {
        jobId: string;
        invoiceId: string;
        then: InvoiceRefreshIntent;
        vars: UpdateInvoicesMutationVariables;
      } => ({
        invoiceId,
        jobId,
        then: action.then,
        vars: {
          invoices: {
            invoiceId,
            updatePricingTables: true,
          },
        },
      })),
      exhaustMap(({ vars, jobId, invoiceId, then }) => updateInvoicesGQL.mutate(vars).pipe(
        map(() => JobInvoiceActions.updateInvoiceSuccess({ jobId, invoiceId, then })),
        catchError((error) => {
          if (isDocumentRefreshRefusedError(error)) {
            return of(JobInvoiceActions.updateInvoiceRefused({ jobId, invoiceId, then, reason: outdatedInvoiceReason(error) }));
          }
          notify.apolloError('Failed to update invoice', error);
          return of(JobInvoiceActions.updateInvoiceError({ error, invoiceId }));
        }),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const downloadInvoice = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobInvoiceActions.downloadInvoiceRequested,
        JobInvoiceActions.updateInvoiceSuccess,
        JobInvoiceActions.updateInvoiceRefused,
      ),
      filter((action: any) => {
        if (action.type === JobInvoiceActions.downloadInvoiceRequested.type) {
          return true;
        }
        return action.then === 'download';
      }),
      concatLatestFrom((action) => store.select(createSelector(
        selectInvoicesDictionary,
        selectActionsMenuInvoice,
        (invoices, selectedInvoice) => {
          if (action.type !== JobInvoiceActions.downloadInvoiceRequested.type) {
            return invoices[action.invoiceId];
          }
          return selectedInvoice;
        },
      ))),
      filter(([ _action, selectedInvoice ]) => Boolean(selectedInvoice?.artifacts?.[0])),
      tap(([ action, selectedInvoice ]) => {
        const refused = action.type === JobInvoiceActions.updateInvoiceRefused.type;
        if (refused) {
          documentHelper.warnInvoiceOutdated(action.reason);
        }
        documentHelper.downloadDocument(selectedInvoice.artifacts[0] as any, { outdated: refused });
      }),
    );
}, { functional: true, dispatch: false });

export const openInvoiceInRightPanel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobInvoiceActions.invoiceComponentClicked,
        JobInvoiceActions.openInvoiceInRightPanelMenuItemClicked,
      ),
      concatLatestFrom((action) => store.select(createSelector(
        jobToolFeature.selectActionsMenuInvoiceId,
        (actionsMenuInvoiceId) => {
          if (action.type === JobInvoiceActions.openInvoiceInRightPanelMenuItemClicked.type) {
            return actionsMenuInvoiceId;
          } else {
            return action.invoiceId;
          }
        }
      ))),
      tap(([ _action, invoiceId ]) => detailsHelper.detailsItem.next({
          type: 'invoice',
          item: {
            id: invoiceId,
          }
        })),
    );
}, { functional: true, dispatch: false });

export const openSelectedInvoiceInRightPanel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.openInvoiceInRightPanelMenuItemClicked),
      concatLatestFrom(() => store.select(selectActionsMenuInvoice)),
      tap(([ _action, selectedInvoice ]) => {
        detailsHelper.detailsItem.next({
          type: 'invoice',
          item: {
            id: selectedInvoice.id,
          }
        })
      }),
    );
}, { functional: true, dispatch: false });

export const confirmOpenInvoiceInPandadoc = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  getInvoiceLinksGQL = inject(GetInvoiceLinksGQL),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobInvoiceActions.viewInvoiceAsCustomerMenuItemClicked,
        JobInvoiceActions.viewInvoiceAsEmployeeMenuItemClicked
      ),
      concatLatestFrom(() => [
        store.select(selectActionsMenuInvoice),
        store.select(selectJobId),
      ]),
      tap(([ action, invoice, jobId ]) => {

        const viewAsEmployee = action.type === JobInvoiceActions.viewInvoiceAsEmployeeMenuItemClicked.type;

        const dispatchOpenInvoiceSuccess$ = getInvoiceLinksGQL.mutate(({
          invoiceIds: [ invoice.id ],
          role: viewAsEmployee ? 'employee' : 'customer',
        })).pipe(
          map((res) => res.data.getLinks[0]),
          tap((link) => store.dispatch(JobInvoiceActions.getInvoiceLinkSuccess({
            jobId,
            link,
            invoiceId: invoice.id,
            promptedBy: action,
          }))),
        );

        const document = invoice?.artifacts?.[0];

        if (isFinalizedInvoice(invoice) || isAegisArtifact(document)) {
          dispatchOpenInvoiceSuccess$.subscribe();
          return;
        }

        const message = `Opening an invoice will finalize it. `
        + `You will not be able to make any further charges on any invoiced events. `
        + `If you just want to view the document and do not need to collect signatures, `
          + `consider using the "Preview" option instead.`;

        freyaHelper.confirmWithLoading({
          header: 'Open Invoice?',
          message,
          acceptLabel: 'Open and Finalize',
          acceptIcon: 'pi pi-external-link',
          rejectLabel: 'Preview',
          rejectIcon: 'pi pi-eye',
          acceptObservable: dispatchOpenInvoiceSuccess$,
          reject: () => freyaHelper.openInDialog(document),
          onAcceptSuccess: () => {},
        });

          return from(dispatchOpenInvoiceSuccess$);
      }),
    );
}, { functional: true, dispatch: false });

export const openInvoiceInPandadoc = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.getInvoiceLinkSuccess),
      filter(({ promptedBy }) => [
        JobInvoiceActions.viewInvoiceAsCustomerMenuItemClicked,
        JobInvoiceActions.viewInvoiceAsEmployeeMenuItemClicked
      ].map((a) => a.type).includes(promptedBy.type as any)),
      concatLatestFrom(({ invoiceId }) => store.select(createSelector(
        selectInvoices,
        (invoices) => invoices.find((i) => i.id === invoiceId),
      ))),
      map(([ { link }, invoice ]) => ({
        document: invoice?.artifacts?.[0],
        link,
      })),
      tap(({ document, link }) => freyaHelper.openInDialog(document, link)),
    );
  }, { functional: true, dispatch: false });

export const voidInvoice = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateInvoicesGQL = inject(UpdateInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.voidInvoiceMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectActionsMenuInvoice),
        store.select(selectJobId),
      ]),
      map(([ _action, invoice, jobId ]): {
        vars: UpdateInvoicesMutationVariables;
        jobId: string;
        invoiceId: string;
      } => {

        return {
          jobId,
          invoiceId: invoice.id,
          vars: {
            invoices: {
              invoiceId: invoice.id,
              voided: true,
            },
          },
        }
      }),
      switchMap(({ jobId, invoiceId, vars }) => updateInvoicesGQL.mutate(vars).pipe(
        map(() => {
          notify.success('Invoice voided');
          return JobInvoiceActions.voidInvoiceSuccess({ jobId, invoiceId });
        }),
        catchError((error) => {
          notify.apolloError('Failed to void invoice', error);
          return of(JobInvoiceActions.voidInvoiceError({ error }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);


export const deleteInvoice = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  deleteInvoiceGQL = inject(DeleteInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.deleteInvoiceMenuItemClicked),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectActionsMenuInvoiceId),
        store.select(selectJobId),
      ]),
      map(([ _action, invoiceId, jobId ]): {
        jobId: string;
        invoiceId: string;
        vars: DeleteInvoicesMutationVariables;
      } => {
        return {
          jobId,
          invoiceId,
          vars: {
            invoiceIds: [ invoiceId ],
          },
        }
      }),
      switchMap(({ vars, jobId, invoiceId }) => deleteInvoiceGQL.mutate(vars).pipe(
        map(() => {
          notify.success('Invoice deleted');
          return JobInvoiceActions.deleteInvoiceSuccess({ jobId, invoiceId });
        }),
        catchError((error) => {
          notify.apolloError('Failed to delete invoice', error);
          return of(JobInvoiceActions.deleteInvoiceError({ error }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

export const finalizeInvoice = createEffect((
  actions$ = inject(Actions),
  finalizeInvoicesGQL = inject(FinalizeInvoicesGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.finalizeInvoiceMenuItemClicked),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectActionsMenuInvoice),
          store.select(selectJobId),
        ]),
        filter(([ _action, invoice ]) => isDraftInvoice(invoice)),
        mergeMap(([ _action, invoice, jobId ]) => finalizeInvoicesGQL.mutate({ invoiceIds: [ invoice.id ] }).pipe(
          tap((res) => {
            if (!res.data?.finalizeInvoices) {
              throw new Error('Failed to finalize invoice');
            }
          }),
          map(() => JobInvoiceActions.finalizeInvoiceSuccess({ jobId, invoiceId: invoice.id })),
        )),
        catchError((error) => of(JobInvoiceActions.finalizeInvoiceError({ error }))),
      )),
    );
},
{ functional: true, dispatch: true },
);

export const forceUpdateInvoice = createEffect((
  actions$ = inject(Actions),
  updateInvoicesGQL = inject(UpdateInvoicesGQL),
  store = inject(Store),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.forceUpdateInvoiceMenuItemClicked),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectActionsMenuInvoiceId),
        store.select(selectJobId),
      ]),
      map(([ _action, invoiceId, jobId ]): {
        jobId: string;
        invoiceId: string;
        vars: UpdateInvoicesMutationVariables;
      } => ({
        invoiceId,
        jobId,
        vars: {
          invoices: {
            invoiceId,
            updatePricingTables: true,
          },
        },
      })),
      exhaustMap(({ vars, jobId, invoiceId, }) => updateInvoicesGQL.mutate(vars).pipe(
        tap(() => notify.success('Invoice updated')),
        map(() => JobInvoiceActions.forceUpdateInvoiceSuccess({ jobId, invoiceId })),
        catchError((error) => {
          notify.apolloError('Failed to update invoice', error);
          return of(JobInvoiceActions.forceUpdateInvoiceError({ error }));
        }),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const exportInvoiceToQuickbooks = createEffect((
  actions$ = inject(Actions),
  exportInvoiceToQuickbooksGQL = inject(ExportInvoiceToQuickbooksGQL),
  store = inject(Store),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.exportInvoiceToQuickbooksMenuItemClicked),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectActionsMenuInvoiceId),
        store.select(selectJobId),
      ]),
      map(([ _action, invoiceId, jobId ]): {
        jobId: string;
        invoiceId: string;
        vars: ExportInvoiceToQuickbooksMutationVariables;
      } => ({
        invoiceId,
        jobId,
        vars: { invoiceId },
      })),
      switchMap(({ jobId, invoiceId, vars }) => exportInvoiceToQuickbooksGQL.mutate(vars).pipe(
        tap(() => notify.success('Invoice exported to Quickbooks')),
        map((res) => JobInvoiceActions.exportInvoiceToQuickbooksSuccess({
          invoiceId,
          jobId,
          quickbooksId: res.data.exportInvoiceToQuickbooks,
        })),
        catchError((error) => {
          notify.apolloError('Failed to export invoice to Quickbooks', error);
          return of(JobInvoiceActions.exportInvoiceToQuickbooksError({ error }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

export const sendInvoice = createEffect((
  actions$ = inject(Actions),
  sendInvoicesGQL = inject(SendInvoicesGQL),
  store = inject(Store),
  notify = inject(FreyaNotificationsService),
) => {

  return actions$.pipe(
    ofType(JobInvoiceActions.sendInvoiceButtonClicked),
    concatLatestFrom(() => store.select(selectJobId)),
    map(([ { invoiceId }, jobId ]) => ({
      invoiceId,
      jobId,
      vars: { invoiceIds: [ invoiceId ] },
    })),
    exhaustMap(({ vars, invoiceId, jobId }) => sendInvoicesGQL.mutate(vars).pipe(
      tap(() => notify.success('Invoice sent')),
      map(({ data: { sendInvoices: { updated } } }) => JobInvoiceActions.sendInvoiceSuccess({
        invoiceId,
        jobId,
        updated,
      })),
      catchError((error) => {
        notify.apolloError('Failed to send invoice', error);
        return of(JobInvoiceActions.sendInvoiceError({
          error,
          invoiceId,
        }));
      }),
    ),
  ));
  },
{ functional: true, dispatch: true },
);

